import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    TextField,
    Typography,
    withStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import {
    endpoint,
    fwArtifactFamilies,
    fwCapabilityReasonLabels,
    fwProvenanceKindLabels,
    snackbarDefaultValue,
    snackbarVariant,
    tableTranslations,
} from '../../utils/constants';
import { formatFwDate } from '../../utils/fwHelpers';
import {
    fieldErrors,
    isCancelled,
    isValidArtifactFile,
    isValidRevision,
    normalizeRevision,
    operationKey,
    probeWorkflow,
    refusalOf,
    refusalText,
    shortDigest,
    workflowModes,
} from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';
import FwFileSources from './FwFileSources';

const formatBytes = size => {
    if (size == null) return '';
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${size} B`;
};

const capabilityText = capability => {
    if (!capability) return '';
    if (capability.transfer_enabled) return 'Dozwolony';

    return `Zablokowany: ${fwCapabilityReasonLabels[capability.reason] ?? 'powód nieznany'}`;
};

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        padding: '1rem 2rem',
        display: 'flex',
        flexDirection: 'column',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginBottom: '1rem',
        alignItems: 'center',
    },
    table: {
        fontFamily: 'inherit',
        width: '100%',
        flex: 1,
        minHeight: 0,
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
    fileInput: {
        display: 'none',
    },
    fileRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem',
        flexWrap: 'wrap',
    },
    dialogField: {
        marginBottom: '1rem',
    },
};

const FileActionsRenderer = ({ data, context }) => (
    <div>
        {context.isWorkflow && (
            <Button size="small" color="primary" onClick={() => context.onSources(data)}>
                Źródła
            </Button>
        )}
        <IconButton
            size="small"
            disabled={data.tasks_count > 0}
            title={data.tasks_count > 0 ? 'Plik jest używany przez zadania' : 'Usuń plik'}
            onClick={() => context.onDelete(data)}>
            <DeleteIcon />
        </IconButton>
    </div>
);

const emptyIdentity = () => ({
    family: fwArtifactFamilies[0],
    targetRevision: '',
    provenanceKind: 'operator_declared',
    provenanceReference: '',
});

const FwFiles = ({ classes }) => {
    const [mode, setMode] = useState(workflowModes.LOADING);
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [identity, setIdentity] = useState(emptyIdentity);
    const [selectedUpload, setSelectedUpload] = useState(null);
    const [uploadErrors, setUploadErrors] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [probeRefusal, setProbeRefusal] = useState(null);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);
    const uploadKey = useRef(operationKey());
    const requestSource = useRef(null);
    const uploadInput = useRef(null);

    const isWorkflow = mode === workflowModes.CONFIGURED;

    const showMessage = (message, variant) => {
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    };

    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);

    const fetchFiles = (config = {}) => {
        setIsLoading(true);
        axiosInstance
            .get(endpoint.FW_FILES, config)
            .then(res => setFiles(res.data?.data ?? res.data ?? []))
            .catch(err => {
                if (isCancelled(err)) return;
                setFiles([]);
                showMessage(refusalText(refusalOf(err), 'Błąd pobierania plików'), snackbarVariant.ERROR);
            })
            .finally(() => setIsLoading(false));
    };

    const load = () => {
        setMode(workflowModes.LOADING);
        requestSource.current?.cancel();
        requestSource.current = cancelToken.source();
        const config = { cancelToken: requestSource.current.token };
        probeWorkflow(config).then(result => {
            if (result.mode === workflowModes.LOADING) return;
            setProbeRefusal(result.refusal ?? null);
            setMode(result.mode === workflowModes.CONFIGURED ? workflowModes.CONFIGURED : result.mode);
            if (result.mode !== workflowModes.ERROR) fetchFiles(config);
        });
    };

    useEffect(() => {
        load();

        return () => requestSource.current?.cancel();
    }, []);

    const resetUploadForm = () => {
        setIdentity(emptyIdentity());
        setSelectedUpload(null);
        setUploadErrors({});
        uploadKey.current = operationKey();
    };

    const openAddDialog = () => {
        resetUploadForm();
        setIsDialogOpen(true);
    };

    const closeAddDialog = () => {
        if (isUploading) return;
        setIsDialogOpen(false);
    };

    const handleUploadFileChange = event => {
        const chosen = event.target.files?.[0] ?? null;
        event.target.value = '';
        setSelectedUpload(chosen);
        setUploadErrors({});
        uploadKey.current = operationKey();
    };

    const changeIdentity = (field, value) => {
        uploadKey.current = operationKey();
        setIdentity(current => ({ ...current, [field]: value }));
    };

    const submitUpload = () => {
        const nextErrors = {};
        const revision = normalizeRevision(identity.targetRevision);

        if (!selectedUpload) {
            nextErrors.file = 'Wybierz plik .bin';
        } else if (isWorkflow) {
            if (!isValidArtifactFile(selectedUpload.name, identity.family)) {
                nextErrors.file = `Nazwa pliku musi zaczynać się od ${identity.family} i kończyć .bin`;
            }
            if (!isValidRevision(revision)) {
                nextErrors.targetRevision = 'Rewizja musi mieć 1 do 16 bajtów w zapisie szesnastkowym';
            }
        }

        if (Object.keys(nextErrors).length > 0) {
            setUploadErrors(nextErrors);
            return;
        }

        setUploadErrors({});

        const formData = new FormData();
        formData.append('file', selectedUpload);

        if (isWorkflow) {
            formData.append('family', identity.family);
            formData.append('target_revision', revision);
            formData.append('target_provenance[kind]', identity.provenanceKind);
            if (identity.provenanceReference.trim() !== '') {
                formData.append('target_provenance[reference]', identity.provenanceReference.trim());
            }
        }

        setIsUploading(true);
        axiosInstance
            .post(endpoint.FW_FILES, formData, {
                headers: { 'Content-Type': 'multipart/form-data', 'Idempotency-Key': uploadKey.current },
            })
            .then(() => {
                setIsDialogOpen(false);
                resetUploadForm();
                showMessage('Plik wgrany', snackbarVariant.SUCCESS);
                fetchFiles();
            })
            .catch(err => {
                const refusal = refusalOf(err);
                const serverErrors = fieldErrors(refusal);
                setUploadErrors({
                    ...serverErrors,
                    targetRevision: serverErrors.revision ?? serverErrors.targetRevision,
                });
                showMessage(
                    refusal.status === 409
                        ? 'Identyczne bajty są już zapisane jako inny artefakt albo plik jest wycofany'
                        : refusalText(refusal, 'Błąd wgrywania pliku'),
                    snackbarVariant.ERROR
                );
            })
            .finally(() => setIsUploading(false));
    };

    const removeFile = file => {
        if (!window.confirm(`Usunąć plik "${file.filename}"?`)) return;

        axiosInstance
            .delete(`${endpoint.FW_FILES}/${file.id}`, {
                headers: { 'Idempotency-Key': operationKey() },
            })
            .then(() => {
                showMessage('Plik usunięty', snackbarVariant.SUCCESS);
                fetchFiles();
            })
            .catch(err => {
                const refusal = refusalOf(err);
                showMessage(refusalText(refusal, 'Błąd usuwania pliku'), snackbarVariant.ERROR);
            });
    };

    const header = <TitleHeader title="Moduł FW — Obsługa plików .bin" />;

    if (mode === workflowModes.LOADING) {
        return (
            <div className={classes.root}>
                {header}
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (mode === workflowModes.ERROR) {
        return (
            <div className={classes.root}>
                {header}
                <Typography variant="h6">Nie można odczytać katalogu artefaktów</Typography>
                <Typography>
                    {refusalText(probeRefusal, 'Nie udało się odczytać konfiguracji modułu FW')}
                </Typography>
                <div className={classes.actions}>
                    <Button variant="outlined" color="primary" onClick={load}>
                        Spróbuj ponownie
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            {header}
            <div className={classes.actions}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openAddDialog}>
                    Dodaj plik
                </Button>
            </div>
            {isLoading ? (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            ) : (
                <div className={'ag-theme-alpine'} style={styles.table}>
                    <AgGridReact
                        localeText={tableTranslations}
                        rowData={files}
                        suppressCellSelection
                        pagination
                        paginationPageSize={50}
                        enableCellTextSelection
                        accentedSort
                        suppressNoRowsOverlay={!isLoading}
                        context={{
                            onDelete: removeFile,
                            onSources: setSelectedFile,
                            isWorkflow,
                        }}>
                        <AgGridColumn field="id" headerName="ID" width={60} sortable filter resizable />
                        <AgGridColumn
                            field="filename"
                            headerName="Nazwa pliku"
                            flex={1}
                            sortable
                            filter
                            resizable
                        />
                        {isWorkflow && (
                            <AgGridColumn
                                field="family"
                                headerName="Rodzina"
                                width={100}
                                sortable
                                filter
                                resizable
                            />
                        )}
                        {isWorkflow && (
                            <AgGridColumn
                                field="target_revision"
                                headerName="Rewizja docelowa"
                                width={110}
                                sortable
                                filter
                                resizable
                            />
                        )}
                        <AgGridColumn
                            field="file_size"
                            headerName="Rozmiar"
                            width={110}
                            valueFormatter={params => formatBytes(params.value)}
                            sortable
                            filter
                            resizable
                        />
                        <AgGridColumn
                            field="file_crc32"
                            headerName="CRC-32"
                            width={110}
                            sortable
                            filter
                            resizable
                        />
                        {isWorkflow && (
                            <AgGridColumn
                                field="content_sha256"
                                headerName="SHA-256"
                                width={140}
                                valueFormatter={params => shortDigest(params.value)}
                                sortable
                                filter
                                resizable
                            />
                        )}
                        {isWorkflow && (
                            <AgGridColumn
                                field="capability"
                                headerName="Transfer"
                                width={220}
                                valueFormatter={params => capabilityText(params.value)}
                                sortable={false}
                                resizable
                            />
                        )}
                        <AgGridColumn
                            field="created_at"
                            headerName="Data dodania"
                            width={180}
                            valueFormatter={params => formatFwDate(params.value)}
                            sortable
                            filter
                        />
                        <AgGridColumn
                            headerName=""
                            width={170}
                            sortable={false}
                            filter={false}
                            cellRendererFramework={FileActionsRenderer}
                        />
                    </AgGridReact>
                </div>
            )}
            <Dialog
                open={isDialogOpen}
                onClose={closeAddDialog}
                aria-labelledby="fw-add-file-title"
                fullWidth
                maxWidth="sm">
                <DialogTitle id="fw-add-file-title">Dodaj plik</DialogTitle>
                <DialogContent dividers>
                    <div className={classes.fileRow}>
                        <input
                            ref={uploadInput}
                            id="fw-upload-file"
                            type="file"
                            accept=".bin"
                            aria-label="Plik .bin"
                            onChange={handleUploadFileChange}
                            className={classes.fileInput}
                        />
                        <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            startIcon={<CloudUploadIcon />}
                            disabled={isUploading}
                            onClick={() => uploadInput.current?.click()}>
                            Wybierz plik .bin
                        </Button>
                        <Typography variant="body2">
                            {selectedUpload ? selectedUpload.name : 'Nie wybrano pliku'}
                        </Typography>
                    </div>
                    {uploadErrors.file && (
                        <Typography color="error" role="alert">
                            {uploadErrors.file}
                        </Typography>
                    )}
                    {isWorkflow && (
                        <>
                            <TextField
                                id="fw-upload-family"
                                select
                                className={classes.dialogField}
                                label="Rodzina"
                                value={identity.family}
                                onChange={event => changeIdentity('family', event.target.value)}
                                variant="outlined"
                                fullWidth>
                                {fwArtifactFamilies.map(family => (
                                    <MenuItem key={family} value={family}>
                                        {family}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                id="fw-upload-revision"
                                className={classes.dialogField}
                                label="Rewizja docelowa"
                                value={identity.targetRevision}
                                onChange={event => changeIdentity('targetRevision', event.target.value)}
                                error={Boolean(uploadErrors.targetRevision)}
                                helperText={uploadErrors.targetRevision || 'Na przykład 01 lub A1B2'}
                                variant="outlined"
                                fullWidth
                            />
                            <TextField
                                id="fw-upload-provenance"
                                select
                                className={classes.dialogField}
                                label="Pochodzenie rewizji"
                                value={identity.provenanceKind}
                                onChange={event => changeIdentity('provenanceKind', event.target.value)}
                                variant="outlined"
                                fullWidth>
                                {Object.entries(fwProvenanceKindLabels).map(([kind, label]) => (
                                    <MenuItem key={kind} value={kind}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                id="fw-upload-reference"
                                className={classes.dialogField}
                                label="Odniesienie (opcjonalnie)"
                                value={identity.provenanceReference}
                                onChange={event => changeIdentity('provenanceReference', event.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAddDialog} disabled={isUploading}>
                        Anuluj
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={submitUpload}
                        disabled={isUploading || !selectedUpload}>
                        Wgraj
                    </Button>
                </DialogActions>
            </Dialog>
            <FwFileSources
                file={selectedFile}
                open={Boolean(selectedFile)}
                onClose={() => setSelectedFile(null)}
            />
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </div>
    );
};

export default withStyles(styles)(FwFiles);
